import { Button } from '@dfh-corp-sites/ui'
import { cn } from '@dfh-corp-sites/ui/utils'
import { Subscribe } from '@react-rxjs/core'
import BlockContent from '@sanity/block-content-to-react'
import React, { ImgHTMLAttributes } from 'react'

import { SanityDispensaryBio, SanityImage } from '../../../graphql/gatsby'
import { SanityImage as GroqSanityImage } from '../../../groq/sanity-dispensaries/types'
import useIsSsr from '../../../hooks/useIsSsr'
import { useLogAndCaptureLeftResult } from '../../../hooks/useLogAndCaptureLeftResult'
import { useDispensaryContext } from '../../../lib/dispensaries'
import { useDispensaryIsContactFilled } from '../../../lib/dispensaries/hooks/useDispensaryIsContactFilled'
import { useDispensaryOrDefaultValues } from '../../../lib/dispensaries/hooks/useDispensaryOrDefaultValues'
import { dispensaryLogoSrcProps } from '../../../lib/dispensaries/logo'
import { dispensaryImageUrlFor, useSanityRoleBasedContent } from '../../../lib/sanity'
import { latestDispensaryContact$ } from '../../../lib/sanity-dispensaries/contact'
import { DFHLogo } from '../../global/header/ui'
import Link from '../../global/Link'
import Spinner from '../../Spinner'
import { serializers } from '../DispensaryBio/serializers'

export interface VirtualDispensaryProps extends SanityDispensaryBio {
  className?: string
  sideImage?: SanityImage
}

const Container = ({ children }: { children: React.ReactNode }) => {
  return <div className="max-w-[1280px] mx-auto px-10">{children}</div>
}

const bioImageHeight = (width: number) => Math.round((width * 550) / 718)

const bioImageSrcProps = (
  image: GroqSanityImage,
): Pick<ImgHTMLAttributes<HTMLImageElement>, 'sizes' | 'src' | 'srcSet'> => {
  const src368 =
    dispensaryImageUrlFor(image.asset._ref)
      .size(368, bioImageHeight(368))
      .fit('max')
      .auto('format')
      .url() || ''
  const src400 =
    dispensaryImageUrlFor(image.asset._ref)
      .size(400, bioImageHeight(400))
      .fit('max')
      .auto('format')
      .url() || ''
  const src600 =
    dispensaryImageUrlFor(image.asset._ref)
      .size(600, bioImageHeight(600))
      .fit('max')
      .auto('format')
      .url() || ''
  const src800 =
    dispensaryImageUrlFor(image.asset._ref)
      .size(800, bioImageHeight(800))
      .fit('max')
      .auto('format')
      .url() || ''
  const src1000 =
    dispensaryImageUrlFor(image.asset._ref)
      .size(1000, bioImageHeight(1000))
      .fit('max')
      .auto('format')
      .url() || ''
  const src1200 =
    dispensaryImageUrlFor(image.asset._ref)
      .size(1200, bioImageHeight(1200))
      .fit('max')
      .auto('format')
      .url() || ''
  const src1400 =
    dispensaryImageUrlFor(image.asset._ref)
      .size(1400, bioImageHeight(1400))
      .fit('max')
      .auto('format')
      .url() || ''
  const src1600 =
    dispensaryImageUrlFor(image.asset._ref)
      .size(1600, bioImageHeight(1600))
      .fit('max')
      .auto('format')
      .url() || ''
  return {
    sizes: '(max-width: 767px) 368w, (max-width: 1439px) 50vw, 718px',
    src: src800,
    srcSet: `${src368} 368w, ${src400} 400w, ${src600} 600w, ${src800} 800w, ${src1000} 1000w, ${src1200} 1200w, ${src1400} 1400w, ${src1600} 1600w`,
  }
}

const AboutUs = () => {
  const dispensary = useDispensaryContext()
  const dispensarySlug = dispensary?.slug || ''

  const sanityDispensaryResult = useDispensaryOrDefaultValues()

  useLogAndCaptureLeftResult(sanityDispensaryResult)

  const contact =
    sanityDispensaryResult._tag === 'Right' ? sanityDispensaryResult.data?.contact : undefined

  const dispensaryResult =
    sanityDispensaryResult._tag === 'Right' ? sanityDispensaryResult.data : undefined

  const isContactFilled = useDispensaryIsContactFilled()

  const getPath = (path: string) => {
    const isDispensary = !!dispensary
    return isDispensary ? `/u/${dispensarySlug}${path}` : path
  }

  return (
    <div className=" border-l-4 border-brand-orange">
      <Container>
        <div className="mt-6 md:mt-12">
          {dispensaryResult?.logo?.image?.asset?._ref ? (
            <img
              className="w-auto h-[90px] object-contain"
              alt="Dispensary logo"
              {...dispensaryLogoSrcProps(dispensaryResult.logo.image)}
            />
          ) : (
            <DFHLogo />
          )}
        </div>

        <div className="max-w-[970px] w-full py-10">
          <h1 className={cn('text-brand-navy text-3xl leading-[120%]', 'md:text-6xl')}>
            <span className="font-medium">
              Welcome to {contact?.displayName || dispensary?.name}’s
            </span>{' '}
            Virtual Dispensary
          </h1>
        </div>
      </Container>

      <div className={cn('flex flex-col mt-6', 'md:flex-row md:mt-12')}>
        {/* The bio image is called hero image */}
        {dispensaryResult?.bio?.image?.asset?._ref && (
          <img
            className={cn('w-full h-[282px] object-contain md:w-[50%] md:h-[550px]')}
            alt=""
            {...bioImageSrcProps(dispensaryResult.bio.image)}
          />
        )}

        <div className={cn('w-full px-10', 'md:w-[50%]')}>
          <div aria-hidden className="h-[1px] w-full bg-brand-rule-grey" />

          <h3 className="mt-12 uppercase text-brand-navy text-xl font-medium">about us</h3>

          <p className="mt-6 text-xl whitespace-pre-line">
            <BlockContent serializers={serializers} blocks={dispensaryResult?.bio?.content} />
          </p>

          {isContactFilled && (
            <Button className="mt-12" asChild>
              <Link to={getPath('/practitioner-about')}>Learn more</Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

const VirtualDispensary: React.FC<VirtualDispensaryProps> = ({ roleBasedContent }) => {
  // Suspense not supported server-side yet
  const isSsr = useIsSsr()
  const { shouldDisplay } = useSanityRoleBasedContent({ roleBasedContent })

  if (!shouldDisplay || isSsr) {
    return null
  }

  return (
    <Subscribe source$={latestDispensaryContact$} fallback={<Spinner loading />}>
      <AboutUs />
    </Subscribe>
  )
}

export default VirtualDispensary
